import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiSearch, 
  FiCalendar,
  FiX,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import { HiChevronDoubleLeft, HiChevronLeft, HiChevronRight, HiChevronDoubleRight } from 'react-icons/hi';
import { fetchProjects, deleteProject } from '../services/api';
import MetricInfoButton from '../components/MetricInfoButton';
import { FiTrash2 } from 'react-icons/fi';

const Projects = () => {

  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const formatDateFromIso = (isoDate) => {
    if (!isoDate) return 'Unknown date';
    
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };
  
  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    
    return tokens.toString();
  };
  
  const handleCheckboxChange = (id) => {
    if (selectedProjects.includes(id)) {
      setSelectedProjects(selectedProjects.filter(projectId => projectId !== id));
    } else {
      setSelectedProjects([...selectedProjects, id]);
    }
  };
  
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedProjects(projects.map(project => project.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Helper function to get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Helper function to get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Check if a date has projects
  const hasProjectsOnDate = (date) => {
    return projects.some(project => 
      project.dateObj.getDate() === date.getDate() &&
      project.dateObj.getMonth() === date.getMonth() &&
      project.dateObj.getFullYear() === date.getFullYear()
    );
  };

  // Format date as string (e.g., "May 1, 2025")
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const getFilteredProjects = () => {
    let filtered = projects;

    // Filter by search query if it exists
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(project => 
        project.name.toLowerCase().includes(query) || 
        (project.models && project.models.some(model => model.toLowerCase().includes(query))) ||
        (project.status && project.status.toLowerCase().includes(query)) ||
        (project.tokenUsage && project.tokenUsage.toLowerCase().includes(query))
      );
    }

    // Only filter by date if user selects a date
    if (selectedDate) {
      filtered = filtered.filter(project => {
        if (!project.dateObj) return false;
        const projectDate = project.dateObj;
        const matches = (
          projectDate.getDate() === selectedDate.getDate() &&
          projectDate.getMonth() === selectedDate.getMonth() &&
          projectDate.getFullYear() === selectedDate.getFullYear()
        );
        return matches;
      });
    }

    return filtered;
  };

  // Generate calendar UI
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const monthNames = ["January", "February", "March", "April", "May", "June", 
                        "July", "August", "September", "October", "November", "December"];
    
    // Create calendar grid
    const days = [];
    const blanks = [];
    
    // Add blank spaces for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      blanks.push(<div key={`blank-${i}`} className="p-2"></div>);
    }
    
    // Add days of month
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const hasProjects = hasProjectsOnDate(date);
      const isSelected = selectedDate && 
                         selectedDate.getDate() === d && 
                         selectedDate.getMonth() === month &&
                         selectedDate.getFullYear() === year;
      
      days.push(
        <div 
          key={`day-${d}`} 
          onClick={() => handleDateClick(date)}
          className={`
            p-2 text-center cursor-pointer rounded-full w-8 h-8 mx-auto flex items-center justify-center
            hover:bg-blue-100
            ${hasProjects ? 'font-semibold' : 'text-gray-500'}
            ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
          `}
        >
          {d}
        </div>
      );
    }
    
    // Combine blanks and days
    const totalSlots = [...blanks, ...days];
    const rows = [];
    let cells = [];
    
    // Create rows with 7 cells each (for each day of week)
    totalSlots.forEach((slot, i) => {
      if (i % 7 !== 0) {
        cells.push(slot);
      } else {
        rows.push(cells);
        cells = [];
        cells.push(slot);
      }
      if (i === totalSlots.length - 1) {
        rows.push(cells);
      }
    });
    
    return (
      <div className="p-4">
        {/* Month navigation */}
        <div className="flex justify-between items-center mb-4">
          <button onClick={() => navigateMonth(-1)} className="text-gray-500 hover:text-gray-700">
            <FiChevronLeft />
          </button>
          <h3 className="font-medium">{monthNames[month]} {year}</h3>
          <button onClick={() => navigateMonth(1)} className="text-gray-500 hover:text-gray-700">
            <FiChevronRight />
          </button>
        </div>
        
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="text-center text-xs text-gray-500 font-medium">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div>
          {rows.map((row, index) => (
            <div key={index} className="grid grid-cols-7 mb-2">
              {row}
            </div>
          ))}
        </div>
        
        {/* Selected date display and clear button */}
        {selectedDate && (
          <div className="mt-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Selected: {formatDate(selectedDate)}
            </span>
            <button 
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={clearDateFilter}
            >
              Clear
            </button>
          </div>
        )}
      </div>
    );
  };

  const modelBadgeColor = (model) => {
    const modelLower = model.toLowerCase();
    switch (modelLower) {
      case 'gpt-4':
      case 'gpt-4o':
        return 'bg-blue-100 text-blue-600';
      case 'gpt-4o-large':
        return 'bg-indigo-100 text-indigo-600';
      case 'gpt-4o-mini':
        return 'bg-cyan-100 text-cyan-600';
      case 'gpt-3.5-turbo':
        return 'bg-green-100 text-green-600';
      case 'claude':
      case 'claude-3':
        return 'bg-purple-100 text-purple-600';
      case 'llama-3':
        return 'bg-amber-100 text-amber-600';
      case 'mistral-7b':
        return 'bg-red-100 text-red-600';
      case 'custom llm-x':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const loadRealProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchProjects();
      // Handle both cases: when fetchProjects returns an array directly or an object with projects property
      const projects = Array.isArray(response) ? response : (response?.projects || []);
      if (projects && projects.length > 0) {
        // Transform API data to match our project structure
        const formattedProjects = projects.map((project, index) => ({
            id: project.id || project._id || project.project_id || index + 1,
          name: project.name || project.project_name || `Project ${index + 1}`,
          date: formatDateFromIso(project.created_at),
          dateObj: project.created_at ? new Date(project.created_at) : new Date(),
          models: project.models_used || [],
            tokenUsage: formatTokens(project.token_usage || project.tokens_used || project.total_tokens),
            latency: `${Math.round((project.avg_latency || 0))} ms`,
          clients: project.client_count || 0,
            status: project.status || 'active',
            cost: project.total_cost || 0,
            runCount: project.run_count || project.total_runs || 0
        }));
        const sortedProjects = formattedProjects.sort((a, b) => {
          const dateA = new Date(a.dateObj);
          const dateB = new Date(b.dateObj);
          return dateB - dateA;
        });
        setProjects(sortedProjects);
      } else {
          setError('No projects found in database.');
      }
    } catch (err) {
        setError('Failed to load projects from database. Please check your connection.');
      } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch real projects from database (MongoDB, DynamoDB, or ClickHouse)
    loadRealProjects();
  }, [loadRealProjects]);

  // Listen for project data updates (e.g., when a run is deleted)
  useEffect(() => {
    const handleProjectDataUpdated = () => {
      loadRealProjects();
    };
    window.addEventListener('projectDataUpdated', handleProjectDataUpdated);
    return () => {
      window.removeEventListener('projectDataUpdated', handleProjectDataUpdated);
    };
  }, [loadRealProjects]);

  const handleDeleteProject = async (projectId, projectName) => {
    setDeleting(true);
    try {
      await deleteProject(projectId);
      // Remove from local state
      setProjects(projects.filter(p => p.id !== projectId));
      setDeleteConfirm(null);
      // Optionally reload all projects
      await loadRealProjects();
    } catch (error) {
      setError(`Failed to delete project: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const filteredProjects = getFilteredProjects();

  return (
    <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-blue-800">Projects</h1>
              <MetricInfoButton metricType="projects-overview" />
            </div>
          </div>
          {/* Search and Filter */}
          <div className="flex justify-end items-center mb-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search projects..."
                  className="pl-10 pr-4 py-2 rounded-md border border-gray-300 w-72"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                {searchQuery && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="relative">
                <button 
                  className={`flex items-center px-4 py-2 rounded-md ${selectedDate ? 'bg-blue-600 text-white' : 'border border-blue-600 text-blue-600'}`}
                  onClick={toggleCalendar}
                >
                  <FiCalendar className="mr-2" />
                  {selectedDate ? formatDate(selectedDate) : 'Select Date'}
                </button>
                
                {showCalendar && (
                  <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-10 w-80">
                    <div className="flex justify-between items-center p-4 border-b">
                      <h3 className="font-medium">Calendar</h3>
                      <button 
                        className="text-gray-400 hover:text-gray-600"
                        onClick={toggleCalendar}
                      >
                        <FiX />
                      </button>
                    </div>
                    {renderCalendar()}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Loading and Error States */}
          {loading && (
            <div className="bg-white rounded-md shadow-sm p-8 text-center">
              <p className="text-gray-600">Loading projects...</p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 rounded-md shadow-sm p-4 mb-6">
              <p className="text-red-600">{error}</p>
            </div>
          )}
          {/* Project Table */}
          {!loading && !error && (
            <div className="bg-white rounded-md shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-sm text-gray-500">
                    <th className="p-4 text-left">
                      <input 
                        type="checkbox" 
                        className="rounded"
                        onChange={handleSelectAll}
                        checked={selectedProjects.length === projects.length}
                      />
                    </th>
                    <th className="p-4 text-left font-medium flex items-center">
                      Project Name
                      <button className="ml-1">
                        <img src="/images/svg/chevron-down.svg" alt="Expand" className="w-3 h-3" />
                      </button>
                    </th>
                    <th className="p-4 text-left font-medium">
                      <div className="flex items-center">
                        Created Date
                        <button className="ml-1">
                          <img src="/images/svg/chevron-down.svg" alt="Expand" className="w-3 h-3" />
                        </button>
                      </div>
                    </th>
                    <th className="p-4 text-left font-medium">
                      <div className="flex items-center">
                        Models
                        <button className="ml-1">
                          <img src="/images/svg/chevron-down.svg" alt="Expand" className="w-3 h-3" />
                        </button>
                      </div>
                    </th>
                    <th className="p-4 text-left font-medium">Token Usage</th>
                    <th className="p-4 text-left font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-gray-500">
                        No projects found matching your criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredProjects.map(project => (
                      <tr key={project.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="p-4">
                          <input 
                            type="checkbox" 
                            className="rounded"
                            checked={selectedProjects.includes(project.id)}
                            onChange={() => handleCheckboxChange(project.id)}
                          />
                        </td>
                        <td className="p-4 font-medium">
                          <Link to={`/projects/${project.id}`} className="text-blue-600 hover:underline">
                            {project.name}
                          </Link>
                        </td>
                        <td className="p-4 text-gray-500">{project.date}</td>
                        <td className="p-4">
                          <div className="flex gap-1">
                            {project.models.map((model, idx) => (
                              <span key={idx} className={`${modelBadgeColor(model)} text-xs px-2 py-1 rounded`}>
                                {model}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-gray-500">{project.tokenUsage}</td>
                        <td className="p-4">
                          <button
                            onClick={() => setDeleteConfirm({ id: project.id, name: project.name })}
                            className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors"
                            title="Delete project"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="border-t border-gray-200 p-4 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  Showing {filteredProjects.length} of {projects.length} projects
                </div>
                <div className="flex items-center space-x-2">
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100">
                    <HiChevronDoubleLeft className="w-5 h-5" />
                  </button>
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100">
                    <HiChevronLeft className="w-5 h-5" />
                  </button>
                  <button className="border border-gray-300 rounded-md w-8 h-8 flex items-center justify-center bg-blue-600 text-white">
                    1
                  </button>
                  <button className="border border-gray-300 rounded-md w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-100">
                    2
                  </button>
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100">
                    <HiChevronRight className="w-5 h-5" />
                  </button>
                  <button className="border border-gray-300 rounded-md p-1 text-gray-500 hover:bg-gray-100">
                    <HiChevronDoubleRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Project</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone and will delete all associated runs and data.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteProject(deleteConfirm.id, deleteConfirm.name)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects; 